/*
 * <atc-product-card> — a product card on the "add to cart" collection template.
 * ---------------------------------------------------------------------------
 * Self-initializing custom element. This matters: facets-el.js replaces the
 * innerHTML of #collection-page-container on every filter and sort change, and
 * script tags outside that container are not re-run. Wiring everything in
 * connectedCallback means each freshly-inserted card sets itself up again.
 *
 * Every shopper-facing option with more than one value gets its own pill row,
 * and each row owns one position in the variant's option tuple. Positions with
 * no pill row (a single-valued option, or one hidden by the
 * `card_hidden_option_names` theme setting) stay pinned to the values of the
 * variant the card loaded with.
 *
 * Bundle cards render no variant JSON and no pills, so this element no-ops on
 * them.
 */
class AtcProductCard extends HTMLElement {
  /** Matches `products.product.add_to_cart` from theme.liquid, without the price token. */
  static addToCartLabel() {
    const noPrice = window.variantStrings?.addToCartNoPrice?.trim();
    if (noPrice) return noPrice;

    const raw = window.variantStrings?.addToCart || 'Add to cart';
    return raw
      .replace(/\{\{\s*price\s*\}\}/gi, '')
      .replace(/\[\s*price\s*\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  static soldOutLabel() {
    return window.variantStrings?.soldOut || 'Sold out';
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const dataEl = this.querySelector('.atc-card__variants');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.variants = [];
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    this.idInput = this.querySelector('input[name="id"]');
    this.image = this.querySelector('.atc-card__image');
    this.mediaLink = this.querySelector('.atc-card__media-link');
    // Image, title and View product all deep-link to the chosen variant.
    this.variantLinks = Array.from(this.querySelectorAll('.atc-card__variant-link'));
    this.submitButton = this.querySelector('.atc-card__add');
    this.submitText = this.querySelector('.atc-card__add-text');
    this.priceEl = this.querySelector('.atc-card__price .price');
    this.defaultImage = this.dataset.defaultImage || '';
    this.productUrl = this.dataset.productUrl || '';

    // One group per rendered pill row. `position` is the 0-based index into the
    // variant option tuple, so two rows never fight over the same slot.
    this.optionGroups = Array.from(this.querySelectorAll('.atc-card__options'))
      .map((fieldset) => ({
        position: parseInt(fieldset.dataset.optionPosition, 10),
        inputs: Array.from(fieldset.querySelectorAll('input[data-atc-option]')),
      }))
      .filter((group) => !Number.isNaN(group.position));

    // Seed the option tuple from the variant the card rendered with, so option
    // positions without pills keep their server-side values.
    const initialId = parseInt(this.idInput?.value, 10);
    const initial = this.variants.find((v) => v.id === initialId) || this.variants[0];
    this.selected = Array.isArray(initial.options) ? initial.options.slice() : [];

    this.optionGroups.forEach((group) => {
      group.inputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.selected[group.position] = input.value;
          this.updateVariant();
        });
      });
    });
  }

  updateVariant() {
    const match = this.variants.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length === this.selected.length &&
        v.options.every((o, i) => o === this.selected[i])
    );

    // No variant for this combination — leave the card on its last good state
    // rather than half-updating it.
    if (!match) return;

    if (this.idInput) this.idInput.value = match.id;

    this.updateImage(match);
    this.updatePrice(match);

    // Built by hand, not from variant.url — Shopify only fills that in for the
    // selected variant, so every other entry came back without ?variant= and
    // the links went to the plain product page. Single-variant products keep
    // the plain URL.
    // `&` when the URL already carries Shopify's ?_pos=&_fid=&_ss= tracking
    // params, which it does inside a collection listing. A second `?` makes the
    // browser fold `variant` into the preceding parameter and the deep link
    // silently resolves to the default variant.
    if (this.variants.length > 1 && this.productUrl) {
      const separator = this.productUrl.includes('?') ? '&' : '?';
      const href = `${this.productUrl}${separator}variant=${match.id}`;
      this.variantLinks.forEach((link) => link.setAttribute('href', href));
    }

    if (this.submitButton) {
      this.submitButton.disabled = !match.available;
      if (this.submitText) {
        this.submitText.textContent = match.available
          ? AtcProductCard.addToCartLabel()
          : AtcProductCard.soldOutLabel();
      }
    }
  }

  updateImage(match) {
    if (!this.image) return;

    const src = match.image || this.defaultImage;
    if (!src || this.image.getAttribute('src') === src) return;

    // The initial <img> comes from image_tag and carries a srcset/sizes pair
    // that would keep winning over the src we set here.
    this.image.removeAttribute('srcset');
    this.image.removeAttribute('sizes');
    this.image.setAttribute('src', src);
  }

  /*
   * The money strings are formatted in Liquid and carried in the variant JSON,
   * not built here: the storefront is multi-currency and multi-locale, and
   * `money_with_currency` / `money_without_trailing_zeros` already know the
   * shop's format for the current request.
   *
   * price.liquid renders both a `.price__regular` and a `.price__sale` copy of
   * the amount and lets `.price--on-sale` decide which one is visible, so both
   * are written on every change. The struck-out compare-at price is not
   * rendered at all at `location: 'product-card'`, so there is nothing else to
   * keep in sync.
   */
  updatePrice(match) {
    if (!this.priceEl || !match.price) return;

    const regular = this.priceEl.querySelector('.price__regular .price-item--regular');
    const sale = this.priceEl.querySelector('.price__sale .price-item--sale');
    if (regular) regular.textContent = match.price;
    if (sale) sale.textContent = match.price;

    this.priceEl.classList.toggle('price--on-sale', match.on_sale === true);
    this.priceEl.classList.toggle('price--sold-out', match.available !== true);
  }
}

if (!customElements.get('atc-product-card')) {
  customElements.define('atc-product-card', AtcProductCard);
}
